import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { formatPrice } from "@/lib/utils";
import { getImageUrl } from "@/lib/images";
import { Plus, Edit2, Power, ExternalLink, Share2, Building2, X, Trash2, ChevronDown, ChevronRight, GripVertical, Check } from "lucide-react";
import { toast } from "sonner";

const isCompleted = (b) => {
  const paid = b.payment_status && !b.payment_status.startsWith("pending");
  const endHour = b.end_hour;
  const dateStr = b.date;
  const now = new Date();
  const bookingEnd = new Date(dateStr + "T" + String(Math.floor(endHour)).padStart(2, "0") + ":" + String(Math.round((endHour % 1) * 60)).padStart(2, "0") + ":00");
  return b.status === "confirmed" && paid && bookingEnd < now;
};

const getCourtStats = (c, bookings) => {
  const courtBookings = bookings.filter((b) => b.court_id === c.id);
  const courtRevenue = courtBookings
    .filter((b) => b.status === "confirmed" || isCompleted(b))
    .reduce((sum, b) => sum + (b.total_price || 0), 0);

  const thisWeekStart = (() => { const d = new Date(); d.setDate(d.getDate() - d.getDay()); const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0"); const day = String(d.getDate()).padStart(2, "0"); return `${y}-${m}-${day}`; })();
  let totalAvailableHours = 0;
  let totalBookedHours = 0;
  for (let d = 0; d < 7; d++) {
    const date = (() => { const dt = new Date(thisWeekStart); dt.setDate(dt.getDate() + d); const y = dt.getFullYear(); const m = String(dt.getMonth() + 1).padStart(2, "0"); const day = String(dt.getDate()).padStart(2, "0"); return `${y}-${m}-${day}`; })();
    const wh = (c.weekly_hours || []).find((wh) => wh.day === d);
    if (wh?.is_closed) continue;
    const open = wh?.open_hour ?? c.open_hour;
    const close = wh?.close_hour ?? c.close_hour;
    if (open === undefined || close === undefined) continue;
    totalAvailableHours += close - open;
    courtBookings
      .filter((b) => b.date === date && (b.status === "confirmed" || isCompleted(b)))
      .forEach((b) => { totalBookedHours += (b.end_hour || 0) - (b.start_hour || 0); });
  }
  const utilization = totalAvailableHours > 0 ? Math.round((totalBookedHours / totalAvailableHours) * 100) : 0;

  return { courtBookings, courtRevenue, utilization };
};

export default function CourtsTab({ courts, bookings, onRefresh }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // View mode
  const [viewMode, setViewMode] = useState('grid');

  // Grid view state (from original CourtsTab)
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [facilityFilter, setFacilityFilter] = useState('');
  const [sortBy, setSortBy] = useState('default');

  // Facility form state (from original FacilitiesTab)
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [phone, setPhone] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [contactVis, setContactVis] = useState("confirmed_only");
  const [facilityAmenities, setFacilityAmenities] = useState([]);
  const [facilityRules, setFacilityRules] = useState("");
  const [allAmenities, setAllAmenities] = useState([]);
  const [loading, setLoading] = useState(false);

  // By-facility view state (from original FacilitiesTab)
  const [expandedId, setExpandedId] = useState(null);
  const [bookingsData, setBookingsData] = useState(bookings || []);
  const [bookingsLoading, setBookingsLoading] = useState(false);

  // Inline editing state
  const [editingCourtId, setEditingCourtId] = useState(null);
  const [editCourtName, setEditCourtName] = useState("");
  const [editCourtSport, setEditCourtSport] = useState("");
  const [editCourtActive, setEditCourtActive] = useState(true);
  const editRef = useRef(null);

  // Batch action state
  const [batchOpenHour, setBatchOpenHour] = useState(8);
  const [batchCloseHour, setBatchCloseHour] = useState(22);
  const [batchCancelHours, setBatchCancelHours] = useState(24);
  const [batchCancelFee, setBatchCancelFee] = useState(10);
  const [batchDeposit, setBatchDeposit] = useState(0);
  const [showBatchConfirm, setShowBatchConfirm] = useState(null);
  const [batchFacilityId, setBatchFacilityId] = useState(null);

  // Drag-and-drop state
  const [dragCourtId, setDragCourtId] = useState(null);
  const [dragOverCourtId, setDragOverCourtId] = useState(null);
  const [courtOrderMap, setCourtOrderMap] = useState({});

  // Manage Facilities modal state
  const [showManageModal, setShowManageModal] = useState(false);

  // ---- Data loading ----

  const loadFacilities = async () => {
    setLoading(true);
    try {
      const r = await api.get("/owner/facilities");
      if (!Array.isArray(r.data)) {
        setFacilities([]);
      } else {
        setFacilities(r.data);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFacilities();
    api.get("/amenities").then((r) => setAllAmenities(r.data || [])).catch((e) => {
      if (import.meta.env.DEV) console.warn("amenities load failed:", e);
    });
  }, []);

  // Keep bookingsData in sync with prop
  useEffect(() => {
    if (bookings) {
      setBookingsData(bookings);
    }
  }, [bookings]);

  // Fetch bookings if not passed as prop and expanded
  useEffect(() => {
    if (!bookings && expandedId) {
      setBookingsLoading(true);
      api.get("/owner/bookings")
        .then((r) => setBookingsData(r.data || []))
        .catch(() => setBookingsData([]))
        .finally(() => setBookingsLoading(false));
    }
  }, [bookings, expandedId]);

  // Handle click outside to cancel editing
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (editRef.current && !editRef.current.contains(e.target)) {
        setEditingCourtId(null);
      }
    };
    const handleEsc = (e) => {
      if (e.key === "Escape") setEditingCourtId(null);
    };
    if (editingCourtId !== null) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEsc);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [editingCourtId]);

  // ---- Court actions (Grid view) ----

  const toggleActive = async (court) => {
    const msg = court.active ? t("owner.disable_confirm") : t("owner.reactivate_confirm");
    if (!window.confirm(msg)) return;
    try {
      await api.patch(`/courts/${court.id}`, { active: !court.active });
      toast.success(court.active ? t("common.court_disabled") : t("common.court_activated"));
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleAssignFacility = async (facilityId) => {
    if (!selectedCourt) return;
    try {
      await api.patch(`/courts/${selectedCourt.id}`, { facility_id: facilityId });
      toast.success(t("owner.facility_assign_success"));
      setShowAssignModal(false);
      setSelectedCourt(null);
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // ---- Sorting (Grid view) ----

  const sortCourts = (courtList) => {
    if (sortBy === 'default') return courtList;
    return [...courtList].sort((a, b) => {
      const statsA = getCourtStats(a, bookingsData);
      const statsB = getCourtStats(b, bookingsData);
      if (sortBy === 'revenue') return statsB.courtRevenue - statsA.courtRevenue;
      if (sortBy === 'utilization') return statsB.utilization - statsA.utilization;
      if (sortBy === 'bookings') return statsB.courtBookings.length - statsA.courtBookings.length;
      return 0;
    });
  };

  // ---- Facility CRUD ----

  const openCreate = () => {
    setEditing(null);
    setName("");
    setDescription("");
    setPhone("");
    setWhatsapp("");
    setContactVis("confirmed_only");
    setFacilityAmenities([]);
    setFacilityRules("");
    setShowForm(true);
    setShowManageModal(false);
  };

  const openEdit = (f) => {
    setEditing(f);
    setName(f.name);
    setDescription(f.description || "");
    setPhone(f.phone_number || "");
    setWhatsapp(f.whatsapp_number || "");
    setContactVis(f.contact_visibility || "confirmed_only");
    setFacilityAmenities(f.amenities || []);
    setFacilityRules(f.rules || "");
    setShowForm(true);
    setShowManageModal(false);
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    try {
      const payload = {
        name,
        description,
        phone_number: phone,
        whatsapp_number: whatsapp,
        contact_visibility: contactVis,
        amenities: facilityAmenities,
        rules: facilityRules,
      };
      if (editing) {
        await api.patch(`/owner/facilities/${editing.id}`, payload);
        toast.success(t("owner.facility_updated"));
      } else {
        await api.post("/owner/facilities", payload);
        toast.success(t("owner.facility_created"));
      }
      setShowForm(false);
      await loadFacilities();
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleDelete = async (f) => {
    if (!window.confirm(t("owner.facility_delete_confirm", { name: f.name }))) return;
    try {
      await api.delete(`/owner/facilities/${f.id}`);
      toast.success(t("owner.facility_deleted"));
      loadFacilities();
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // ---- By-facility view helpers ----

  const unassignCourt = async (courtId) => {
    try {
      await api.patch(`/courts/${courtId}`, { facility_id: "" });
      toast.success(t("owner.facility_court_unassigned"));
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const startEditCourt = (c, e) => {
    e.stopPropagation();
    setEditingCourtId(c.id);
    setEditCourtName(c.name);
    setEditCourtSport(c.sport);
    setEditCourtActive(c.active);
  };

  const saveCourtEdit = async (courtId) => {
    try {
      await api.patch(`/courts/${courtId}`, {
        name: editCourtName,
        sport: editCourtSport,
        active: editCourtActive,
      });
      toast.success(t("owner.facility_court_edit_saved"));
      setEditingCourtId(null);
      await loadFacilities();
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const courtsByFacility = (facilityId) =>
    (courts || []).filter((c) => c.facility_id === facilityId);

  const getOrderedCourts = (facilityId) => {
    const facilityCourts = courtsByFacility(facilityId);
    const order = courtOrderMap[facilityId];
    if (!order) return facilityCourts;
    const ordered = order.map((id) => facilityCourts.find((c) => c.id === id)).filter(Boolean);
    const remaining = facilityCourts.filter((c) => !order.includes(c.id));
    return [...ordered, ...remaining];
  };

  const handleDragStart = (courtId) => {
    setDragCourtId(courtId);
  };

  const handleDragOver = (e, courtId) => {
    e.preventDefault();
    setDragOverCourtId(courtId);
  };

  const handleDragLeave = () => {
    setDragOverCourtId(null);
  };

  const handleDrop = async (facilityId, targetCourtId) => {
    setDragOverCourtId(null);
    if (!dragCourtId || dragCourtId === targetCourtId) return;

    const facilityCourts = courtsByFacility(facilityId);
    const order = courtOrderMap[facilityId] || facilityCourts.map((c) => c.id);
    const fromIdx = order.indexOf(dragCourtId);
    const toIdx = order.indexOf(targetCourtId);
    if (fromIdx === -1 || toIdx === -1) return;

    const newOrder = [...order];
    newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, dragCourtId);

    try {
      await api.patch(`/owner/facilities/${facilityId}/reorder`, { court_ids: newOrder });
      setCourtOrderMap((prev) => ({ ...prev, [facilityId]: newOrder }));
      toast.success(t("owner.facility_reorder_saved"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setDragCourtId(null);
  };

  const handleDragEnd = () => {
    setDragCourtId(null);
    setDragOverCourtId(null);
  };

  const openBatchConfirm = (facilityId, type) => {
    setBatchFacilityId(facilityId);
    setShowBatchConfirm(type);
    const fc = courtsByFacility(facilityId);
    if (fc.length > 0) {
      if (type === 'hours') {
        setBatchOpenHour(fc[0].open_hour ?? 8);
        setBatchCloseHour(fc[0].close_hour ?? 22);
      } else if (type === 'cancellation') {
        setBatchCancelHours(fc[0].cancellation_hours ?? 24);
        setBatchCancelFee(fc[0].cancellation_fee_percent ?? 10);
      } else if (type === 'deposit') {
        setBatchDeposit(fc[0].deposit_percent ?? 0);
      }
    }
  };

  const executeBatchUpdate = async () => {
    if (!batchFacilityId || !showBatchConfirm) return;
    let payload;
    if (showBatchConfirm === 'hours') {
      payload = { open_hour: batchOpenHour, close_hour: batchCloseHour };
    } else if (showBatchConfirm === 'cancellation') {
      payload = { cancellation_hours: batchCancelHours, cancellation_fee_percent: batchCancelFee };
    } else if (showBatchConfirm === 'deposit') {
      payload = { deposit_percent: batchDeposit };
    }
    try {
      const r = await api.post(`/owner/facilities/${batchFacilityId}/batch-update`, payload);
      toast.success(t("owner.facility_batch_success", { count: r.data.updated }));
      loadFacilities();
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setShowBatchConfirm(null);
    setBatchFacilityId(null);
  };

  const computeFacilityStats = (facilityCourts) => {
    const courtIds = facilityCourts.map((c) => c.id);
    const facilityBookings = (bookingsData || []).filter((b) => courtIds.includes(b.court_id));
    const totalEarnings = facilityBookings
      .filter((b) => b.status === "confirmed")
      .reduce((sum, b) => sum + (b.total_price || 0), 0);
    const totalBookingsCount = facilityBookings.length;
    const activeCount = facilityCourts.filter((c) => c.active).length;
    const totalCount = facilityCourts.length;

    const thisWeekStart = (() => { const d = new Date(); d.setDate(d.getDate() - d.getDay()); const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0"); const day = String(d.getDate()).padStart(2, "0"); return `${y}-${m}-${day}`; })();
    let totalUtilPct = 0;
    let utilCount = 0;
    for (const c of facilityCourts) {
      let totalAvailableHours = 0;
      let totalBookedHours = 0;
      for (let d = 0; d < 7; d++) {
        const date = (() => { const dt = new Date(thisWeekStart); dt.setDate(dt.getDate() + d); const y = dt.getFullYear(); const m = String(dt.getMonth() + 1).padStart(2, "0"); const day = String(dt.getDate()).padStart(2, "0"); return `${y}-${m}-${day}`; })();
        const wh = (c.weekly_hours || []).find((wh) => wh.day === d);
        if (wh?.is_closed) continue;
        const open = wh?.open_hour ?? c.open_hour;
        const close = wh?.close_hour ?? c.close_hour;
        if (open === undefined || close === undefined) continue;
        totalAvailableHours += close - open;
        facilityBookings
          .filter((b) => b.court_id === c.id && b.date === date && (b.status === "confirmed"))
          .forEach((b) => { totalBookedHours += (b.end_hour || 0) - (b.start_hour || 0); });
      }
      if (totalAvailableHours > 0) {
        totalUtilPct += Math.round((totalBookedHours / totalAvailableHours) * 100);
        utilCount++;
      }
    }
    const avgUtilization = utilCount > 0 ? Math.round(totalUtilPct / utilCount) : 0;

    return { totalEarnings, totalBookingsCount, avgUtilization, activeCount, totalCount };
  };

  const StatCard = ({ label, value, accent }) => (
    <div className="border border-border bg-card p-3">
      <div className="text-[10px] text-muted-foreground/80 tracking-widest uppercase mb-1">{label}</div>
      <div className={`font-heading text-xl tracking-tight ${accent ? "text-volt" : "text-foreground"}`}>{value}</div>
    </div>
  );

  // ---- Render helpers ----

  const renderCourtCard = (c) => {
    const { courtBookings, courtRevenue, utilization } = getCourtStats(c, bookingsData);

    return (
      <div key={c.id} className="border border-border bg-card overflow-hidden hover:opacity-100 transition-opacity" data-testid={`owner-court-${c.id}`}>
        <div className="aspect-video overflow-hidden relative">
          <img src={getImageUrl(c.images?.[0], 'card')} alt="" className="w-full h-full object-cover" />
          {!c.active && (
            <div className="absolute top-2 start-2 bg-red-400/90 text-primary-foreground text-[8px] tracking-widest uppercase font-bold px-2 py-0.5">
              {t("owner.inactive")}
            </div>
          )}
          {c.status === "pending" && (
            <div className="absolute top-2 end-2 bg-yellow-400/90 text-primary-foreground text-[8px] tracking-widest uppercase font-bold px-2 py-0.5">
              {t("admin.pending_approval")}
            </div>
          )}
        </div>
        <div className="p-4">
          <div className="font-heading text-2xl tracking-tight">{c.name}</div>
          <div className="text-xs text-muted-foreground mt-1">
            {c.sport} · {formatPrice((c.slot_pricing || {})[String(c.default_slot_duration || c.slot_duration || 60)] ?? c.price_per_slot, c.currency)}/{c.default_slot_duration && c.default_slot_duration !== 60 ? t("court.per_min", { n: c.default_slot_duration }) : t("court.per_hour")}
          </div>
          {c.facility && (
            <div className="text-[10px] text-volt/60 mt-1 tracking-wider flex items-center gap-1">
              <Building2 className="h-3 w-3" /> {c.facility.name}
            </div>
          )}
          <div className="flex items-center gap-4 mt-3 text-[10px] text-muted-foreground/80 tracking-wider uppercase">
            <span>{courtBookings.length} {t("owner.bookings_tab")}</span>
            <span className="text-volt">{formatPrice(courtRevenue, "JOD")}</span>
          </div>
          <div className="mt-3">
            <div className="flex justify-between text-[9px] tracking-widest uppercase mb-1">
              <span className="text-muted-foreground/80">{t("owner.utilization")}</span>
              <span className={utilization > 70 ? "text-emerald-400" : utilization > 40 ? "text-yellow-400" : "text-muted-foreground/80"}>{utilization}%</span>
            </div>
            <div className="h-1.5 bg-background rounded-sm overflow-hidden">
              <div
                className={`h-full rounded-sm transition-all ${utilization > 70 ? "bg-emerald-400" : utilization > 40 ? "bg-yellow-400" : "bg-border/20"}`}
                style={{ width: `${Math.min(utilization, 100)}%` }}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            <button
              onClick={() => navigate(`/owner/courts/${c.id}/edit`)}
              className="btn-outline rounded-sm px-2 py-1.5 text-[9px] flex items-center gap-1"
            >
              <Edit2 className="h-3 w-3" /> {t("owner.edit")}
            </button>
            <button
              onClick={() => toggleActive(c)}
              className={`btn-outline rounded-sm px-2 py-1.5 text-[9px] flex items-center gap-1 ${
                c.active ? "hover:!border-yellow-400 hover:!text-yellow-400" : "hover:!border-emerald-400 hover:!text-emerald-400"
              }`}
            >
              <Power className="h-3 w-3" /> {c.active ? t("owner.disable") : t("owner.reactivate")}
            </button>
            <button
              onClick={() => window.open(`/courts/${c.id}`, "_blank")}
              className="btn-outline rounded-sm px-2 py-1.5 text-[9px] flex items-center gap-1"
              title={t("owner.view_page")}
            >
              <ExternalLink className="h-3 w-3" /> <span className="hidden sm:inline">{t("owner.view_page")}</span>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(`${window.location.origin}/courts/${c.id}`);
                toast.success(t("court.link_copied"));
              }}
              className="btn-outline rounded-sm px-2 py-1.5 text-[9px] flex items-center gap-1"
              title={t("owner.share")}
            >
              <Share2 className="h-3 w-3" /> <span className="hidden sm:inline">{t("owner.share")}</span>
            </button>
            {!c.facility_id && (
              <button
                onClick={() => { setSelectedCourt(c); setShowAssignModal(true); }}
                className="btn-outline rounded-sm px-2 py-1.5 text-[9px] flex items-center gap-1 hover:!border-volt hover:!text-volt"
              >
                <Building2 className="h-3 w-3" /> <span className="hidden sm:inline">{t("owner.facility_assign")}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ================================================================
  // RENDER
  // ================================================================

  return (
    <>
      {/* View Mode Toggle */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-1">
          <button
            onClick={() => setViewMode('grid')}
            className={`border px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold transition-all ${
              viewMode === 'grid'
                ? "border-volt text-volt bg-volt/10"
                : "border-border text-muted-foreground hover:text-foreground hover:border-border"
            }`}
          >
            {t("owner.view_grid")}
          </button>
          <button
            onClick={() => setViewMode('by_facility')}
            className={`border px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold transition-all ${
              viewMode === 'by_facility'
                ? "border-volt text-volt bg-volt/10"
                : "border-border text-muted-foreground hover:text-foreground hover:border-border"
            }`}
          >
            {t("owner.view_by_facility")}
          </button>
        </div>
        <button
          onClick={() => navigate("/owner/courts/new")}
          className="btn-volt rounded-sm px-4 py-2 text-[10px] tracking-widest uppercase font-bold flex items-center gap-2"
        >
          <Plus className="h-3.5 w-3.5" /> {t("owner.add_court")}
        </button>
      </div>

      {/* ================================================================ */}
      {/* GRID VIEW */}
      {/* ================================================================ */}
      {viewMode === 'grid' && (
        <>
          {courts.length === 0 ? (
            <div className="border border-dashed border-border p-16 text-center">
              <p className="font-heading text-3xl text-muted-foreground/80">{t("owner.no_courts")}</p>
              <button
                onClick={() => navigate("/owner/courts/new")}
                className="btn-volt rounded-sm px-5 py-3 text-xs flex items-center gap-2 mx-auto mt-4"
              >
                <Plus className="h-4 w-4" /> {t("owner.add_court")}
              </button>
            </div>
          ) : (
            <>
              {/* Facility filter & Sort & Manage Facilities */}
              <div className="flex items-center gap-4 mb-6 flex-wrap">
                {facilities.length > 0 && (
                  <div className="flex items-center gap-2">
                    <label className="text-[10px] text-muted-foreground/80 tracking-wider uppercase">{t("owner.facility_filter")}</label>
                    <select
                      value={facilityFilter}
                      onChange={(e) => setFacilityFilter(e.target.value)}
                      className="bg-card border border-border px-3 py-1.5 text-xs focus:border-volt focus:outline-none"
                    >
                      <option value="">{t("owner.facility_filter_all")}</option>
                      {facilities.map((f) => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <label className="text-[10px] text-muted-foreground/80 tracking-wider uppercase">{t("owner.sort_by")}</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="bg-card border border-border px-3 py-1.5 text-xs focus:border-volt focus:outline-none"
                  >
                    <option value="default">{t("owner.sort_default")}</option>
                    <option value="revenue">{t("owner.sort_revenue")}</option>
                    <option value="utilization">{t("owner.sort_utilization")}</option>
                    <option value="bookings">{t("owner.sort_bookings")}</option>
                  </select>
                </div>
                <button
                  onClick={() => setShowManageModal(true)}
                  className="border border-border px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold text-muted-foreground hover:text-volt hover:border-volt transition-all flex items-center gap-1.5"
                >
                  <Building2 className="h-3 w-3" /> {t("owner.manage_facilities")}
                </button>
              </div>
              {courts.filter((c) => c.active && (!facilityFilter || (c.facility && c.facility.id === facilityFilter) || c.facility_id === facilityFilter)).length > 0 && (
                <>
                  <div className="label-eyebrow text-volt mb-4">{t("owner.active_courts")}</div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                    {sortCourts(courts.filter((c) => c.active && (!facilityFilter || (c.facility && c.facility.id === facilityFilter) || c.facility_id === facilityFilter))).map((c) => renderCourtCard(c))}
                  </div>
                </>
              )}
              {courts.filter((c) => !c.active && (!facilityFilter || (c.facility && c.facility.id === facilityFilter) || c.facility_id === facilityFilter)).length > 0 && (
                <>
                  <div className="h-px bg-border/30 mb-8" />
                  <div className="label-eyebrow text-muted-foreground/80 mb-4">{t("owner.inactive_courts")}</div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {sortCourts(courts.filter((c) => !c.active && (!facilityFilter || (c.facility && c.facility.id === facilityFilter) || c.facility_id === facilityFilter))).map((c) => renderCourtCard(c))}
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}

      {/* ================================================================ */}
      {/* BY FACILITY VIEW */}
      {/* ================================================================ */}
      {viewMode === 'by_facility' && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <span className="label-eyebrow flex items-center gap-2">
              <Building2 className="h-4 w-4 text-volt" /> {t("owner.facilities")}
            </span>
            <button onClick={openCreate} className="btn-volt rounded-sm px-4 py-2 text-[10px] tracking-widest uppercase font-bold flex items-center gap-1.5">
              <Plus className="h-3.5 w-3.5" /> {t("owner.facility_add")}
            </button>
          </div>

          {loading ? (
            <div className="text-center text-muted-foreground/60 text-sm py-16">{t("common.loading")}</div>
          ) : facilities.length === 0 ? (
            <div className="border border-dashed border-border/30 p-16 text-center">
              <Building2 className="h-8 w-8 mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-muted-foreground/80 text-sm">{t("owner.facility_empty")}</p>
            </div>
          ) : (
            <div className="space-y-6">
              {facilities.map((f) => {
                const facilityCourts = courtsByFacility(f.id);
                const isExpanded = expandedId === f.id;
                return (
                  <div key={f.id} className="border border-border bg-card overflow-hidden">
                    <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => setExpandedId(isExpanded ? null : f.id)}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-volt/10 flex items-center justify-center flex-shrink-0">
                          <Building2 className="h-5 w-5 text-volt" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-heading text-xl tracking-tight">{f.name}</div>
                          <div className="text-[10px] text-muted-foreground/80 tracking-wider">
                            {facilityCourts.length} {t("owner.nav_courts").toLowerCase()} {f.court_count !== undefined ? `· ${t("owner.facility_active_courts", { count: f.court_count })}` : ""}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={(e) => { e.stopPropagation(); openEdit(f); }}
                          className="text-muted-foreground/80 hover:text-volt transition-colors p-1"
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(f); }}
                          className="text-muted-foreground/80 hover:text-red-400 transition-colors p-1"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                        {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground/80" /> : <ChevronRight className="h-4 w-4 text-muted-foreground/80" />}
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="border-t border-border/30 p-4 space-y-4">
                        {/* Aggregate Stats Panel */}
                        {bookingsLoading ? (
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {[1, 2, 3, 4].map((i) => (
                              <div key={i} className="border border-border bg-card p-3 animate-pulse">
                                <div className="h-3 w-16 bg-muted mb-2 rounded" />
                                <div className="h-6 w-20 bg-muted rounded" />
                              </div>
                            ))}
                          </div>
                        ) : (
                          (() => {
                            const stats = computeFacilityStats(facilityCourts);
                            return (
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <StatCard label={t("owner.facility_stats_earnings")} value={formatPrice(stats.totalEarnings, "JOD")} accent />
                                <StatCard label={t("owner.facility_stats_bookings")} value={stats.totalBookingsCount} />
                                <StatCard label={t("owner.facility_stats_utilization")} value={`${stats.avgUtilization}%`} />
                                <StatCard label={t("owner.facility_stats_active")} value={`${stats.activeCount}/${stats.totalCount}`} />
                              </div>
                            );
                          })()
                        )}

                        {f.description && <p className="text-sm text-muted-foreground">{f.description}</p>}

                        {/* Batch Actions */}
                        <div className="border border-border bg-card p-4">
                          <div className="text-[10px] text-muted-foreground/60 tracking-wider mb-3 uppercase">{t("owner.facility_batch_title")}</div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Hours */}
                            <div>
                              <label className="text-[10px] text-muted-foreground tracking-wider block mb-1">{t("owner.facility_batch_hours_label")}</label>
                              <div className="flex items-center gap-2 mb-1">
                                <input
                                  type="number" min={0} max={23}
                                  value={batchOpenHour}
                                  onChange={(e) => setBatchOpenHour(Number(e.target.value))}
                                  className="w-16 bg-card border border-border px-2 py-1.5 text-xs text-center focus:border-volt focus:outline-none"
                                />
                                <span className="text-muted-foreground/60 text-xs">–</span>
                                <input
                                  type="number" min={1} max={24}
                                  value={batchCloseHour}
                                  onChange={(e) => setBatchCloseHour(Number(e.target.value))}
                                  className="w-16 bg-card border border-border px-2 py-1.5 text-xs text-center focus:border-volt focus:outline-none"
                                />
                              </div>
                              <button
                                onClick={() => openBatchConfirm(f.id, 'hours')}
                                className="text-[9px] tracking-widest uppercase font-bold text-volt hover:text-volt/80 transition-colors"
                              >
                                {t("owner.facility_batch_apply")}
                              </button>
                            </div>
                            {/* Cancellation */}
                            <div>
                              <label className="text-[10px] text-muted-foreground tracking-wider block mb-1">{t("owner.facility_batch_cancellation_label")}</label>
                              <div className="flex items-center gap-2 mb-1">
                                <input
                                  type="number" min={0} max={720}
                                  value={batchCancelHours}
                                  onChange={(e) => setBatchCancelHours(Number(e.target.value))}
                                  className="w-16 bg-card border border-border px-2 py-1.5 text-xs text-center focus:border-volt focus:outline-none"
                                  title={t("owner.facility_batch_cancel_hours")}
                                />
                                <span className="text-muted-foreground/60 text-xs">h</span>
                                <input
                                  type="number" min={0} max={100}
                                  value={batchCancelFee}
                                  onChange={(e) => setBatchCancelFee(Number(e.target.value))}
                                  className="w-16 bg-card border border-border px-2 py-1.5 text-xs text-center focus:border-volt focus:outline-none"
                                  title={t("owner.facility_batch_cancel_fee")}
                                />
                                <span className="text-muted-foreground/60 text-xs">%</span>
                              </div>
                              <button
                                onClick={() => openBatchConfirm(f.id, 'cancellation')}
                                className="text-[9px] tracking-widest uppercase font-bold text-volt hover:text-volt/80 transition-colors"
                              >
                                {t("owner.facility_batch_apply")}
                              </button>
                            </div>
                            {/* Deposit */}
                            <div>
                              <label className="text-[10px] text-muted-foreground tracking-wider block mb-1">{t("owner.facility_batch_deposit_label")}</label>
                              <div className="flex items-center gap-2 mb-1">
                                <input
                                  type="number" min={0} max={100}
                                  value={batchDeposit}
                                  onChange={(e) => setBatchDeposit(Number(e.target.value))}
                                  className="w-20 bg-card border border-border px-2 py-1.5 text-xs text-center focus:border-volt focus:outline-none"
                                />
                                <span className="text-muted-foreground/60 text-xs">%</span>
                              </div>
                              <button
                                onClick={() => openBatchConfirm(f.id, 'deposit')}
                                className="text-[9px] tracking-widest uppercase font-bold text-volt hover:text-volt/80 transition-colors"
                              >
                                {t("owner.facility_batch_apply")}
                              </button>
                            </div>
                          </div>
                        </div>

                        {facilityCourts.length > 0 ? (
                          <div>
                            <div className="text-[10px] text-muted-foreground/60 tracking-wider mb-2 uppercase">{t("owner.facility_assigned_courts")}</div>
                            <div ref={editRef} className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
                              {getOrderedCourts(f.id).map((c) => (
                                <div
                                  key={c.id}
                                  draggable={editingCourtId !== c.id}
                                  onDragStart={() => handleDragStart(c.id)}
                                  onDragOver={(e) => handleDragOver(e, c.id)}
                                  onDragLeave={handleDragLeave}
                                  onDrop={() => handleDrop(f.id, c.id)}
                                  onDragEnd={handleDragEnd}
                                  className={`flex items-center gap-3 border p-3 group transition-all ${
                                    editingCourtId === c.id
                                      ? "border-volt bg-card"
                                      : dragOverCourtId === c.id
                                        ? "border-volt/60 bg-volt/5"
                                        : dragCourtId === c.id
                                          ? "border-border/30 bg-card opacity-40"
                                          : "border-border/30 bg-card hover:border-border cursor-pointer"
                                  }`}
                                  onClick={editingCourtId !== c.id ? () => navigate(`/owner/courts/${c.id}/edit`) : undefined}
                                >
                                  {/* Drag handle */}
                                  {editingCourtId !== c.id && (
                                    <div className="text-muted-foreground/40 hover:text-muted-foreground transition-colors cursor-grab active:cursor-grabbing flex-shrink-0">
                                      <GripVertical className="h-4 w-4" />
                                    </div>
                                  )}
                                  <div className="w-10 h-10 rounded-sm overflow-hidden flex-shrink-0 bg-card">
                                    {c.images?.[0] ? <img src={getImageUrl(c.images[0], 'thumbnail')} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground/40 text-[8px]">—</div>}
                                  </div>

                                  {editingCourtId === c.id ? (
                                    /* Inline Edit Mode */
                                    <div className="min-w-0 flex-1 space-y-2" onClick={(e) => e.stopPropagation()}>
                                      <input
                                        value={editCourtName}
                                        onChange={(e) => setEditCourtName(e.target.value)}
                                        className="w-full bg-background border border-input px-2 py-1 text-sm focus:border-volt focus:outline-none"
                                        placeholder={t("owner.court_form.name")}
                                        autoFocus
                                      />
                                      <input
                                        value={editCourtSport}
                                        onChange={(e) => setEditCourtSport(e.target.value)}
                                        className="w-full bg-background border border-input px-2 py-1 text-[10px] focus:border-volt focus:outline-none"
                                        placeholder={t("owner.court_form.sport")}
                                      />
                                      <label className="flex items-center gap-2 text-[10px] text-muted-foreground cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={editCourtActive}
                                          onChange={(e) => setEditCourtActive(e.target.checked)}
                                          className="accent-volt"
                                        />
                                        {t("owner.active_courts")}
                                      </label>
                                      <div className="flex gap-1">
                                        <button
                                          onClick={() => saveCourtEdit(c.id)}
                                          className="text-volt hover:text-volt/80 transition-colors p-1"
                                          title={t("common.save")}
                                        >
                                          <Check className="h-3.5 w-3.5" />
                                        </button>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); setEditingCourtId(null); }}
                                          className="text-muted-foreground/80 hover:text-foreground transition-colors p-1"
                                          title={t("owner.facility_edit_cancel")}
                                        >
                                          <X className="h-3.5 w-3.5" />
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    /* Normal View */
                                    <>
                                      <div className="min-w-0 flex-1">
                                        <div
                                          className="text-sm font-semibold truncate hover:text-volt transition-colors"
                                          onClick={(e) => startEditCourt(c, e)}
                                        >
                                          {c.name}
                                        </div>
                                        <div className="text-[10px] text-muted-foreground/80">{c.sport}</div>
                                      </div>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); startEditCourt(c, e); }}
                                        className="text-muted-foreground/40 hover:text-volt transition-colors opacity-0 group-hover:opacity-100 p-1"
                                        title={t("owner.edit")}
                                      >
                                        <Edit2 className="h-3 w-3" />
                                      </button>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); unassignCourt(c.id); }}
                                        className="text-muted-foreground/40 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100 p-1"
                                        title={t("owner.facility_unassign")}
                                      >
                                        <X className="h-3.5 w-3.5" />
                                      </button>
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground/60">{t("owner.facility_no_courts")}</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ================================================================ */}
      {/* MODALS */}
      {/* ================================================================ */}

      {/* Assign to Facility Modal */}
      {showAssignModal && selectedCourt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80" onClick={() => { setShowAssignModal(false); setSelectedCourt(null); }}>
          <div className="bg-card border border-border w-full max-w-md mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <span className="label-eyebrow">{t("owner.facility_assign_modal_title")}</span>
              <button onClick={() => { setShowAssignModal(false); setSelectedCourt(null); }} className="text-muted-foreground/80 hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground/80 mb-4">
              {t("owner.facility_assign")}: <span className="text-foreground font-semibold">{selectedCourt.name}</span>
            </p>
            {facilities.length === 0 ? (
              <p className="text-sm text-muted-foreground/60 py-4 text-center">{t("owner.facility_empty")}</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {facilities.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => handleAssignFacility(f.id)}
                    className="w-full flex items-center gap-3 p-3 border border-border bg-background hover:border-volt/50 hover:bg-volt/5 transition-all text-left"
                  >
                    <Building2 className="h-4 w-4 text-volt flex-shrink-0" />
                    <div>
                      <div className="text-sm font-medium">{f.name}</div>
                      {f.description && <div className="text-[10px] text-muted-foreground/80 truncate">{f.description}</div>}
                    </div>
                  </button>
                ))}
              </div>
            )}
            <div className="flex justify-end mt-4">
              <button
                onClick={() => { setShowAssignModal(false); setSelectedCourt(null); }}
                className="btn-outline rounded-sm px-4 py-2 text-[10px] tracking-widest uppercase font-bold"
              >
                {t("common.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Facilities Modal */}
      {showManageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80" onClick={() => setShowManageModal(false)}>
          <div className="bg-card border border-border w-full max-w-md mx-4 p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <span className="label-eyebrow">{t("owner.manage_facilities")}</span>
              <button onClick={() => setShowManageModal(false)} className="text-muted-foreground/80 hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>
            <button
              onClick={openCreate}
              className="btn-volt rounded-sm px-4 py-2 text-[10px] tracking-widest uppercase font-bold flex items-center gap-1.5 w-full justify-center mb-4"
            >
              <Plus className="h-3.5 w-3.5" /> {t("owner.facility_add")}
            </button>
            {facilities.length === 0 ? (
              <p className="text-sm text-muted-foreground/60 py-4 text-center">{t("owner.facility_empty")}</p>
            ) : (
              <div className="space-y-2">
                {facilities.map((f) => (
                  <div key={f.id} className="flex items-center justify-between border border-border bg-background p-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{f.name}</div>
                      {f.description && <div className="text-[10px] text-muted-foreground/80 truncate">{f.description}</div>}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                      <button
                        onClick={() => openEdit(f)}
                        className="text-muted-foreground/80 hover:text-volt transition-colors p-1"
                        title={t("owner.edit")}
                      >
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(f)}
                        className="text-muted-foreground/80 hover:text-red-400 transition-colors p-1"
                        title={t("owner.delete")}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Facility Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80" onClick={() => setShowForm(false)}>
          <div className="bg-card border border-border w-full max-w-2xl mx-4 p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <span className="label-eyebrow">{editing ? t("owner.facility_edit") : t("owner.facility_add")}</span>
              <button onClick={() => setShowForm(false)} className="text-muted-foreground/80 hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1.5 block">{t("owner.facility_name")} *</label>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("owner.facility_name_placeholder")} className="w-full bg-background border border-input px-4 py-3 text-sm focus:border-volt focus:outline-none" />
              </div>
              <div>
                <label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1.5 block">{t("owner.facility_description")}</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder={t("owner.facility_desc_placeholder")} className="w-full bg-background border border-input px-4 py-3 text-sm focus:border-volt focus:outline-none resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1.5 block">{t("owner.facility_phone")}</label>
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t("owner.facility_phone_placeholder")} className="w-full bg-background border border-input px-4 py-3 text-sm focus:border-volt focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1.5 block">{t("owner.facility_whatsapp")}</label>
                  <input value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder={t("owner.facility_whatsapp_placeholder")} className="w-full bg-background border border-input px-4 py-3 text-sm focus:border-volt focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1.5 block">{t("owner.facility_contact_visibility")}</label>
                <div className="flex gap-2">
                  {[
                    { v: "always", l: t("owner.facility_contact_always") },
                    { v: "confirmed_only", l: t("owner.facility_contact_confirmed_only") },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setContactVis(opt.v)}
                      className={`px-3 py-2 text-[10px] tracking-widest uppercase font-bold border transition-all ${
                        contactVis === opt.v
                          ? "border-volt text-volt bg-volt/10"
                          : "border-border/30 text-muted-foreground hover:text-foreground hover:border-border"
                      }`}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1.5 block">{t("owner.facility_amenities_label")}</label>
                <div className="flex flex-wrap gap-1.5">
                  {allAmenities.map((a) => {
                    const active = facilityAmenities.includes(a.key);
                    return (
                      <button
                        key={a.key}
                        type="button"
                        onClick={() => setFacilityAmenities(active ? facilityAmenities.filter((k) => k !== a.key) : [...facilityAmenities, a.key])}
                        className={`px-2.5 py-1 text-[10px] tracking-wider font-bold border transition-all ${
                          active
                            ? "border-volt text-volt bg-volt/10"
                            : "border-border/30 text-muted-foreground/80 hover:text-foreground hover:border-border"
                        }`}
                      >
                        {t(`owner.amenities.${a.key}`, a.label || a.key)}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1.5 block">{t("owner.facility_rules_label")}</label>
                <textarea value={facilityRules} onChange={(e) => setFacilityRules(e.target.value)} rows={3} placeholder={t("owner.facility_rules_placeholder")} className="w-full bg-background border border-input px-4 py-3 text-sm focus:border-volt focus:outline-none resize-none" />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={() => setShowForm(false)} className="btn-outline rounded-sm px-5 py-3 text-xs flex-1">{t("common.cancel")}</button>
              <button onClick={handleSave} disabled={!name.trim()} className="btn-volt rounded-sm px-5 py-3 text-xs flex-1 disabled:opacity-40">{editing ? t("owner.facility_save") : t("owner.facility_create")}</button>
            </div>
          </div>
        </div>
      )}

      {/* Batch confirmation modal */}
      {showBatchConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80" onClick={() => { setShowBatchConfirm(null); setBatchFacilityId(null); }}>
          <div className="bg-card border border-border w-full max-w-md mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="text-foreground text-sm mb-4">
              {showBatchConfirm === 'hours' && t("owner.facility_batch_confirm_hours", { open: batchOpenHour, close: batchCloseHour })}
              {showBatchConfirm === 'cancellation' && t("owner.facility_batch_confirm_cancellation", { hours: batchCancelHours, fee: batchCancelFee })}
              {showBatchConfirm === 'deposit' && t("owner.facility_batch_confirm_deposit", { percent: batchDeposit })}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => { setShowBatchConfirm(null); setBatchFacilityId(null); }}
                className="btn-outline rounded-sm px-5 py-3 text-xs flex-1"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={executeBatchUpdate}
                className="btn-volt rounded-sm px-5 py-3 text-xs flex-1"
              >
                {t("owner.facility_batch_apply")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
